import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  EconChart,
  type EconCurve,
  type EconDot,
  type EconLegendItem,
  type EconShade,
} from "@/components/tools/econ-micro/curve-chart";
import type { SupplyAndDemandStrings } from "@/components/tools/supply-and-demand-calculator";
import { t } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import type { XY } from "@/lib/tool-kit/econ-micro/geometry";
import type {
  Curves,
  Equilibrium,
  Intervention,
} from "@/lib/tool-kit/econ-micro/market";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const DEMAND = CHART_COLORS[0];
const SUPPLY = CHART_COLORS[1];
const CONSUMER = CHART_COLORS[4];
const PRODUCER = CHART_COLORS[2];
const GOVERNMENT = CHART_COLORS[5];
const LOSS = CHART_COLORS[3];

/** The demand and supply lines out to `xMax` (the chart clips them to its box). */
function lines(curves: Curves, xMax: number) {
  const demand: XY[] = [
    [0, curves.demandIntercept],
    [xMax, curves.demandIntercept - curves.demandSlope * xMax],
  ];
  const supply: XY[] = [
    [0, curves.supplyIntercept],
    [xMax, curves.supplyIntercept + curves.supplySlope * xMax],
  ];
  return { demand, supply };
}

/** Surplus triangles at the market's prices and quantity (before or after a wedge). */
function surplusShades(
  curves: Curves,
  quantity: number,
  buyerPrice: number,
  sellerPrice: number
): EconShade[] {
  return [
    {
      id: "cs",
      color: CONSUMER,
      points: [
        [0, curves.demandIntercept],
        [0, buyerPrice],
        [quantity, buyerPrice],
      ],
    },
    {
      id: "ps",
      color: PRODUCER,
      points: [
        [0, curves.supplyIntercept],
        [0, sellerPrice],
        [quantity, sellerPrice],
      ],
    },
  ];
}

export function MarketGraph({
  curves,
  market,
  after,
  strings,
  fmt,
}: {
  curves: Curves;
  market: Equilibrium;
  after: Intervention | null;
  strings: SupplyAndDemandStrings;
  fmt: Fmt;
}) {
  const xMax = curves.demandIntercept / curves.demandSlope;
  const yMax = curves.demandIntercept;
  const { demand, supply } = lines(curves, xMax);
  const e: XY = [market.quantity, market.price];
  const taxed = after && after.perUnit > 0;

  const curvesOut: EconCurve[] = [
    { id: "d", points: demand, color: DEMAND, label: strings.curveDemand },
    { id: "s", points: supply, color: SUPPLY, label: strings.curveSupply },
  ];
  const dots: EconDot[] = [];
  let shades: EconShade[];
  const legend: EconLegendItem[] = [];
  let tableRows: (string | number)[][] = [
    [strings.pointEquilibrium, fmt(market.quantity), fmt(market.price)],
  ];
  if (after) {
    const shift = after.perUnit;
    curvesOut.push({
      id: "shifted",
      points: supply.map(([x, y]) => [x, y + shift] as XY),
      color: SUPPLY,
      dashed: true,
      label: taxed ? strings.curveSupplyTax : strings.curveSupplySubsidy,
    });
    const qt = after.quantity;
    const wedge: EconShade = {
      id: "gov",
      color: GOVERNMENT,
      opacity: 0.45,
      points: [
        [0, after.sellerPrice],
        [qt, after.sellerPrice],
        [qt, after.buyerPrice],
        [0, after.buyerPrice],
      ],
    };
    const loss: EconShade = {
      id: "dwl",
      color: LOSS,
      opacity: 0.6,
      points: [e, [qt, after.buyerPrice], [qt, after.sellerPrice]],
    };
    shades = [
      ...surplusShades(curves, qt, after.buyerPrice, after.sellerPrice),
      wedge,
      loss,
    ];
    dots.push(
      {
        id: "e",
        x: e[0],
        y: e[1],
        label: strings.pointEquilibriumShort,
        position: taxed ? "e" : "n",
      },
      {
        id: "pb",
        x: qt,
        y: after.buyerPrice,
        label: strings.pointBuyers,
        position: taxed ? "nw" : "se",
        guides: true,
      },
      {
        id: "ps",
        x: qt,
        y: after.sellerPrice,
        label: strings.pointSellers,
        position: taxed ? "sw" : "ne",
        guides: true,
      }
    );
    legend.push(
      { label: strings.legendConsumer, color: CONSUMER },
      { label: strings.legendProducer, color: PRODUCER },
      {
        label: taxed ? strings.legendRevenue : strings.legendSubsidyCost,
        color: GOVERNMENT,
      },
      { label: strings.legendLoss, color: LOSS }
    );
    tableRows = [
      ...tableRows,
      [strings.pointBuyers, fmt(qt), fmt(after.buyerPrice)],
      [strings.pointSellers, fmt(qt), fmt(after.sellerPrice)],
    ];
  } else {
    shades = surplusShades(curves, market.quantity, market.price, market.price);
    dots.push({
      id: "e",
      x: e[0],
      y: e[1],
      label: strings.pointEquilibriumShort,
      position: "ne",
      guides: true,
    });
    legend.push(
      { label: strings.legendConsumer, color: CONSUMER },
      { label: strings.legendProducer, color: PRODUCER }
    );
  }

  const title = strings.graphTitle;
  const description = t(strings.graphDescription, {
    demandStart: fmt(curves.demandIntercept, { maximumFractionDigits: 2 }),
    supplyStart: fmt(curves.supplyIntercept, { maximumFractionDigits: 2 }),
    q: fmt(market.quantity, { maximumFractionDigits: 2 }),
    p: fmt(market.price, { maximumFractionDigits: 2 }),
  });
  const head = [strings.tablePoint, strings.quantityAxis, strings.priceAxis];
  return (
    <ChartFigure
      apa={false}
      data={toTsv([head, ...tableRows])}
      figureNumber="1"
      filename={chartFilename(title, "supply-and-demand-graph")}
      labels={strings}
      note=""
      table={{ head, rows: tableRows }}
      title={title}
    >
      <EconChart
        curves={curvesOut}
        description={
          after
            ? `${description} ${t(strings.graphDescriptionAfter, {
                qt: fmt(after.quantity, { maximumFractionDigits: 2 }),
                pb: fmt(after.buyerPrice, { maximumFractionDigits: 2 }),
                ps: fmt(after.sellerPrice, { maximumFractionDigits: 2 }),
              })}`
            : description
        }
        dots={dots}
        fmt={fmt}
        id="sd-chart"
        label={title}
        legend={legend}
        shades={shades}
        xMax={xMax}
        xTitle={strings.quantityAxis}
        yMax={yMax}
        yTitle={strings.priceAxis}
      />
    </ChartFigure>
  );
}
